import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Button, Card, Chip, Divider, Typography } from "@mui/material";

const formatPrice = (pricePaise) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number(pricePaise) % 100 === 0 ? 0 : 2,
  }).format((Number(pricePaise) || 0) / 100);

const formatLimit = (value) => {
  if (value === null || value === undefined) return "Unlimited";
  if (value >= 9999) return "Unlimited";
  return new Intl.NumberFormat("en-IN").format(value);
};

const rupeesToPaise = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
};

const formatFeatureName = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const SubscriptionCard = ({
  planDetails,
  planName,
  serviceType,
  currentPlan,
  onChoose,
  paymentDisabled,
  paymentLoading,
}) => {
  const features = planDetails?.features || {};
  const pricing = planDetails?.pricing || {};
  const payablePricePaise =
    Number(pricing.amount_paise ?? planDetails?.price_paise) || 0;
  const listPricePaise = rupeesToPaise(pricing.list_price) || payablePricePaise;
  const discountPaise = Math.max(
    rupeesToPaise(pricing.discount_amount) ||
      listPricePaise - payablePricePaise,
    0,
  );
  const discountPercentage = Number(pricing.discount_percentage) || 0;
  const gstRate = Number(pricing.gst_rate) || 0;
  const managerType = formatFeatureName(features.manager_service_type);
  const featureRows = [
    features.verified_mobile_limit != null
      ? `${formatLimit(features.verified_mobile_limit)} verified mobile numbers`
      : null,
    features.is_unlimited_profile_views
      ? "Unlimited profile views"
      : features.profile_view_limit != null
        ? `${formatLimit(features.profile_view_limit)} profile views`
        : null,
    features.is_unlimited_messages
      ? "Unlimited messages"
      : features.message_limit != null
        ? `${formatLimit(features.message_limit)} messages`
        : null,
    features.horoscope_access ? "Horoscope access included" : null,
    features.has_relationship_manager
      ? `${managerType || "Dedicated"} relationship manager`
      : "Self-service membership",
    features.support_level
      ? `${formatFeatureName(features.support_level)} support${
          features.support_response_sla_hours
            ? ` · ${features.support_response_sla_hours}-hour response SLA`
            : ""
        }`
      : null,
    features.shortlists_per_month != null
      ? `${formatLimit(features.shortlists_per_month)} shortlists per month`
      : null,
    features.contact_attempts_per_month != null
      ? `${formatLimit(features.contact_attempts_per_month)} contact attempts per month`
      : null,
    features.progress_reports_per_month != null
      ? `${formatLimit(features.progress_reports_per_month)} progress reports per month`
      : null,
    features.meeting_coordination_limit != null
      ? `${formatLimit(features.meeting_coordination_limit)} coordinated meetings`
      : null,
    features.fair_usage_applies
      ? "Unlimited benefits are subject to fair usage"
      : null,
  ].filter(Boolean);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        p: { xs: 2.25, md: 2.75 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        border: "1px solid",
        borderColor: currentPlan ? "success.main" : "rgba(15, 23, 42, 0.1)",
        borderRadius: 3,
        bgcolor: "background.paper",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: { md: "translateY(-6px)" },
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      {currentPlan && (
        <Chip
          label="Current plan"
          color="success"
          size="small"
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontWeight: 750,
          }}
        />
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography color="text.secondary" fontSize={12} fontWeight={700} letterSpacing={0.8}>
            {planName?.toUpperCase()}
          </Typography>
          <Typography variant="h6" fontWeight={750} sx={{ mt: 0.5 }}>
            {planDetails?.duration_label || planDetails?.duration_type}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: 2,
            color: "primary.main",
            bgcolor: "rgba(255, 128, 0, 0.1)",
          }}
        >
          <AccessTimeRoundedIcon fontSize="small" />
        </Box>
      </Box>

      <Box sx={{ mt: 2.25 }}>
        {discountPaise > 0 && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
          >
            <Typography
              component="span"
              color="text.secondary"
              fontSize={14}
              sx={{ textDecoration: "line-through" }}
            >
              {formatPrice(listPricePaise)}
            </Typography>
            <Chip
              label={`Save ${formatPrice(discountPaise)}${
                discountPercentage ? ` (${discountPercentage.toFixed(2)}%)` : ""
              }`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 24, fontWeight: 700 }}
            />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", mt: 0.5 }}>
          <Typography component="span" variant="h4" fontWeight={800} letterSpacing={-1}>
            {formatPrice(payablePricePaise)}
          </Typography>
          <Typography component="span" color="text.secondary" fontSize={13} sx={{ ml: 0.75 }}>
            one-time
            {gstRate
              ? ` · ${gstRate}% GST ${pricing.is_gst_inclusive ? "included" : "applied"}`
              : ""}
          </Typography>
        </Box>
      </Box>

      <Typography color="text.secondary" fontSize={13} sx={{ mt: 1, minHeight: 40 }}>
        {planDetails?.description || formatFeatureName(serviceType)}
      </Typography>

      <Divider sx={{ my: 2.25 }} />

      <Box sx={{ display: "grid", gap: 1.3, flexGrow: 1 }}>
        {featureRows.map((feature) => (
          <Box key={feature} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18, mt: "1px" }} />
            <Typography fontSize={13.5} color="text.primary">
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        endIcon={!paymentLoading && <ArrowForwardRoundedIcon />}
        sx={{ mt: 3, py: 1.15, borderRadius: 2, textTransform: "none", fontWeight: 750 }}
        disabled={paymentDisabled || currentPlan}
        onClick={() => onChoose(planDetails)}
      >
        {currentPlan
          ? "Current plan"
          : paymentLoading
            ? "Opening PhonePe..."
            : "Choose this plan"}
      </Button>
    </Card>
  );
};

export default SubscriptionCard;
